import { State } from '@energy-retailer/constants';

import { formatProductPlanDefaultOffer } from '@/app/lib/utils/format-product-plan-default-offer';
import { Text } from '@/components/text';
import { Tooltip } from '@/components/tooltip';
import { formatProductPlanPrice } from '@/lib/utils/format-product-plan-price';
import { formatProductPlanTariff } from '@/lib/utils/format-product-plan-tariff';
import { getPlanComparisonText } from '@/lib/utils/get-plan-comparison-text';
import { getPriceFrequencyText } from '@/lib/utils/get-price-frequency-text';

import { Button } from '../button';

import { PlanDetailItem } from './plan-detail-item';

import type { PriceFrequency } from '@energy-retailer/constants';
import type { ComponentProps, PropsWithChildren } from 'react';

type PlanDetailListingsProps = PropsWithChildren<
  {
    price: string;
    comparison?: string | null;
    vdoTooltip?: {
      id: string;
      label: string;
      text: string;
    };
    state: State;
    solarFeedTariff?: string;
    superOffPeakTariff?: string;
    priceFrequency?: PriceFrequency;
    hasAddress: boolean;
    addAddress?: () => void;
    setAddAddressActive?: (addAddressActive: boolean) => void;
  } & Pick<ComponentProps<typeof PlanDetailItem>, 'itemType'>
>;

const PlanDetailListings = ({
  comparison,
  price,
  vdoTooltip,
  solarFeedTariff,
  superOffPeakTariff,
  priceFrequency,
  state,
  itemType = 'page',
  hasAddress,
  addAddress,
  setAddAddressActive,
}: PlanDetailListingsProps) => (
  <div className="flex flex-col gap-a">
    {hasAddress ? (
      <>
        <div className="flex flex-row items-stretch gap-a">
          <PlanDetailItem
            itemType={itemType}
            subHeading={`* estimated price per ${getPriceFrequencyText({
              priceFrequency,
            })} (incl GST)`}
          >
            <>
              {formatProductPlanPrice(Number(price) || undefined)}
              <sup>*</sup>
            </>
          </PlanDetailItem>
          <PlanDetailItem
            itemType={itemType}
            subHeading={
              <Text variant="body2">
                {getPlanComparisonText(Number(comparison))}{' '}
                {state === State.VIC ? '' : 'the'}{' '}
                {vdoTooltip ? (
                  <>
                    <a
                      className="text-info underline decoration-info"
                      data-tooltip-id={String(vdoTooltip.id)}
                      aria-describedby={String(vdoTooltip.id)}
                      onTouchStart={(e) => e.stopPropagation()}
                      onTouchEnd={(e) => e.stopPropagation()}
                    >
                      {vdoTooltip.label}
                    </a>
                    <Tooltip id={String(vdoTooltip.id)}>
                      {vdoTooltip.text}
                    </Tooltip>
                  </>
                ) : (
                  'VDO'
                )}
              </Text>
            }
          >
            {formatProductPlanDefaultOffer(Number(comparison))}
          </PlanDetailItem>
        </div>
        <div className="flex flex-row items-stretch gap-a">
          <PlanDetailItem
            itemType={itemType}
            subHeading="Solar Feed-in Tariff"
          >
            {formatProductPlanTariff({
              tariff: Number(solarFeedTariff) || 0,
            })}
          </PlanDetailItem>
          <PlanDetailItem
            itemType={itemType}
            subHeading="super off-peak tariff"
          >
            {formatProductPlanTariff({
              tariff: superOffPeakTariff
                ? Number(superOffPeakTariff)
                : undefined,
              showZero: true,
            })}
          </PlanDetailItem>
        </div>
      </>
    ) : (
      <div className="flex h-[80px] items-center justify-center rounded-rad-a bg-gray-50 text-center">
        <Button
          variant="tertiary"
          onClick={() => {
            if (addAddress && setAddAddressActive) {
              addAddress();
              setAddAddressActive(true);
            }
          }}
        >
          Add your address for accurate pricing
        </Button>
      </div>
    )}
  </div>
);

export { PlanDetailListings };
